/**
 * Company / group / site selection state for the Add User wizard (step 2).
 *
 * Extracted from `pages/InviteUser.tsx` to keep it under 500 lines; behaviour
 * is unchanged.
 */
import { useState, useEffect, useMemo } from 'react';
import { coreAPI, type Company, type CompanyGroup } from '../../lib/api';

export interface InviteCompaniesState {
  loadingOptions: boolean;
  companies: Company[];
  companyGroups: CompanyGroup[];
  primaryCompanyId: number;
  additionalSiteIds: Set<number>;
  setAdditionalSiteIds: (ids: Set<number>) => void;
  /** Encoded dropdown value — 'group:<id>' / 'site:<id>'. */
  primarySelection: string;
  /** Apply a primary-company dropdown pick (group/division or site). */
  selectPrimary: (value: string) => void;
  sitesUnderGroup: (groupId: number) => Company[];
  groupsInScope: CompanyGroup[];
  divisionsUnderGroup: (groupId: number) => CompanyGroup[];
  primaryCompany: Company | undefined;
  sisterSites: Company[];
  selectedSiteIds: number[];
}

/**
 * Load the companies an admin may invite into and track the primary/additional
 * site picks.
 *
 * @param isSupplierInvite - which side the wizard is inviting for.
 * @param ownCompanyId - the admin's own company, preselected when invitable.
 * @param onLoadError - called with a user-facing message if loading fails.
 * @returns The selection state and derived site lists.
 *
 * @example
 * const sites = useInviteCompanies(false, user?.company, setError);
 * sites.selectPrimary('group:12');
 */
export function useInviteCompanies(
  isSupplierInvite: boolean,
  ownCompanyId: number | undefined,
  onLoadError: (message: string) => void,
): InviteCompaniesState {
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  // CR-5313 round 2: Sarah wants the Primary Company dropdown to expose
  // Group + Division levels as well as Sites. We keep the underlying
  // anchor on a single site (UserProfile.company / UserSitePermission
  // rows still target Companies one-by-one), but when an admin picks a
  // Group or Division entry we expand it client-side into "primary =
  // first site under it, additional = every other site under it" so the
  // user effectively gets access across the whole branch.
  const [companyGroups, setCompanyGroups] = useState<CompanyGroup[]>([]);
  const [primaryCompanyId, setPrimaryCompanyId] = useState<number>(0);
  const [additionalSiteIds, setAdditionalSiteIds] = useState<Set<number>>(new Set());
  // Encoded selection — 'group:<id>' / 'site:<id>' — so the dropdown
  // value tracks intent (group vs site) even though storage is
  // site-centric.
  const [primarySelection, setPrimarySelection] = useState<string>('');

  useEffect(() => {
    void loadCompanies();
  }, [isSupplierInvite]);

  const loadCompanies = async () => {
    try {
      setLoadingOptions(true);
      // CR-7071: one scoped source for both invite types — the companies the admin
      // may actually grant into, plus the groups/divisions over them. Replaces the
      // prior getAccessibleCompanies / getInvitableSuppliers + getCompanyGroups combo:
      // the group-list call was Helix-only, so a customer admin got a 403, the rows
      // were dropped, and the picker fell back to a site-only list wider than what the
      // server would accept. This endpoint scopes to the same authority the
      // invitation-create floor enforces, so the picker can't offer a rejectable pick.
      const res = await coreAPI.getInviteOptions(isSupplierInvite ? 'supplier' : 'customer');
      const list = res.data.companies || [];
      setCompanies(list);
      setCompanyGroups(res.data.groups || []);

      // Sensible default for primary: user's own company if it's in the
      // list, otherwise the first entry.
      const ownInList = ownCompanyId && list.find(c => c.id === ownCompanyId);
      if (ownInList) {
        setPrimaryCompanyId(ownInList.id);
        setPrimarySelection(`site:${ownInList.id}`);
      } else if (list.length === 1) {
        setPrimaryCompanyId(list[0].id);
        setPrimarySelection(`site:${list[0].id}`);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      onLoadError('Failed to load companies.');
    } finally {
      setLoadingOptions(false);
    }
  };

  // CR-5313 round 2: return every site whose company-group chain
  // descends from ``groupId`` (the group itself OR any sub-division
  // beneath it). The chain walks via CompanyGroup.parent_group, so
  // selecting the top-level Group reaches Division → Site, and
  // selecting a Division reaches just its sites.
  const sitesUnderGroup = useMemo(() => (groupId: number): Company[] => {
    if (!groupId) return [];
    const descendants = new Set<number>([groupId]);
    let added = true;
    while (added) {
      added = false;
      for (const g of companyGroups) {
        if (g.parent_group != null && descendants.has(g.parent_group) && !descendants.has(g.id)) {
          descendants.add(g.id);
          added = true;
        }
      }
    }
    return companies.filter(c => c.group != null && descendants.has(c.group));
  }, [companies, companyGroups]);

  // CR-5313 round 3 (issue 1): only show groups whose member companies
  // belong to the same type the wizard is inviting. Without this, the
  // Customer invite dropdown surfaced supplier groups (Ironvale,
  // Skybridge, Corex, Northstar) — which a customer admin can't
  // legitimately anchor a user to. ``companies`` is already filtered
  // by the loader (getInviteOptions, scoped per invite type), so a group is
  // "in scope" iff at least one site beneath it appears in ``companies``.
  const groupsInScope = useMemo(() => {
    const visibleCompanyIds = new Set(companies.map(c => c.id));
    return companyGroups.filter(g => {
      const sites = sitesUnderGroup(g.id);
      return sites.some(s => visibleCompanyIds.has(s.id));
    });
  }, [companies, companyGroups, sitesUnderGroup]);

  // CR-5313 round 3 (issue 2): for a group with divisions beneath it,
  // pick the immediate children (divisions) so the "additional sites"
  // panel can render division headers with their sites grouped under
  // each. For a leaf group (no children) this returns an empty list
  // and the panel falls through to the flat site list.
  const divisionsUnderGroup = useMemo(() => (groupId: number): CompanyGroup[] => {
    return companyGroups.filter(g => g.parent_group === groupId);
  }, [companyGroups]);

  // Apply a Group / Division pick by expanding it into the site set
  // we actually anchor to. Primary becomes the first site under the
  // group, additional sites become every other site beneath it.
  const applyGroupSelection = (groupId: number) => {
    const sites = sitesUnderGroup(groupId);
    if (sites.length === 0) {
      setPrimaryCompanyId(0);
      setAdditionalSiteIds(new Set());
      return;
    }
    const [first, ...rest] = sites;
    setPrimaryCompanyId(first.id);
    setAdditionalSiteIds(new Set(rest.map(s => s.id)));
  };

  const selectPrimary = (v: string) => {
    setPrimarySelection(v);
    // Encoded selection: 'group:<id>' anchors at a group/
    // division (expanded client-side into its sites);
    // 'site:<id>' is the legacy site-level anchor.
    if (v.startsWith('group:')) {
      applyGroupSelection(parseInt(v.split(':')[1]));
    } else if (v.startsWith('site:')) {
      setPrimaryCompanyId(parseInt(v.split(':')[1]));
      setAdditionalSiteIds(new Set());
    }
  };

  // CR-5313: only show sister sites in the primary's group as candidates
  // for the multi-site step. Sites in unrelated groups would fail the
  // UserSitePermission.clean() check on the backend.
  // CR-5313 round 2 polish: when the admin anchored via a Group or
  // Division, broaden the sister-sites list to *every* site beneath
  // that anchor so the admin can see (and untick) the full set —
  // otherwise the hint reads "6 sites granted" while only one row
  // appears in the multi-site picker.
  const primaryCompany = useMemo(
    () => companies.find(c => c.id === primaryCompanyId),
    [companies, primaryCompanyId],
  );
  const sisterSites = useMemo(() => {
    if (!primaryCompany) return [];
    if (primarySelection.startsWith('group:')) {
      const anchorId = parseInt(primarySelection.split(':')[1]);
      const beneath = sitesUnderGroup(anchorId);
      return beneath.filter(c => c.id !== primaryCompany.id);
    }
    // A company outside any group has no sister sites. Listing every other
    // invitable company here offered unrelated suppliers as "sites in this group".
    if (!primaryCompany.group) return [];
    return companies.filter(
      c => c.id !== primaryCompany.id && c.group === primaryCompany.group,
    );
  }, [companies, primaryCompany, primarySelection, sitesUnderGroup]);

  const selectedSiteIds = useMemo(() => {
    const ids = new Set<number>(additionalSiteIds);
    if (primaryCompanyId) ids.add(primaryCompanyId);
    return Array.from(ids);
  }, [primaryCompanyId, additionalSiteIds]);

  return {
    loadingOptions, companies, companyGroups,
    primaryCompanyId, additionalSiteIds, setAdditionalSiteIds,
    primarySelection, selectPrimary,
    sitesUnderGroup, groupsInScope, divisionsUnderGroup,
    primaryCompany, sisterSites, selectedSiteIds,
  };
}
